/**
 * Runtime configuration.
 *
 * In local dev, values come from .env via dotenv-webpack (build-time).
 * In deployed environments:
 *   - /config.json (public, pre-auth): Cognito IDs needed to start sign-in
 *   - /app-config.json (behind auth): Lambda name, internal config
 */

interface AppConfig {
  region: string;
  domainId: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  cognitoIdentityPoolId: string;
  cognitoDomain: string;
  catalogApiUrl: string;
  appDomain: string;
  catalogLambdaName: string;
  supportUrl: string;
  jsmSystemField: string;
  jsmPaebblSystemOptionId: string;
}

/** Build-time defaults (from .env for local dev). */
const defaults: AppConfig = {
  region: process.env.REGION ?? "eu-west-1",
  domainId: process.env.DATAZONE_DOMAIN_ID ?? "dzd-amjm7olmkcyl3q",
  cognitoUserPoolId: process.env.COGNITO_USER_POOL_ID ?? "",
  cognitoClientId: process.env.COGNITO_CLIENT_ID ?? "",
  cognitoIdentityPoolId: process.env.COGNITO_IDENTITY_POOL_ID ?? "",
  cognitoDomain: process.env.COGNITO_DOMAIN ?? "",
  catalogApiUrl: process.env.CATALOG_API_URL ?? (process.env.LOCAL_CATALOG === "true" ? "/api/catalog" : ""),
  appDomain: process.env.APP_DOMAIN ?? window?.location?.origin ?? "https://localhost:3000",
  catalogLambdaName: process.env.CATALOG_LAMBDA_NAME ?? "",
  supportUrl: process.env.SUPPORT_URL ?? "https://portal.help.paebbl.systems/servicedesk/customer/portal/7/group/15/create/182",
  jsmSystemField: "customfield_10264",
  jsmPaebblSystemOptionId: "10279",
};

export let config: AppConfig = { ...defaults };

async function fetchJson(path: string): Promise<Record<string, string> | null> {
  try {
    const resp = await fetch(path);
    if (resp.ok) return resp.json();
  } catch { /* not available */ }
  return null;
}

/**
 * Load public config (Cognito IDs). Called before auth.
 */
export async function loadConfig(): Promise<AppConfig> {
  const runtime = await fetchJson("/config.json");
  if (runtime) {
    config = { ...config, ...runtime, appDomain: window?.location?.origin ?? config.appDomain };
  }
  return config;
}

/**
 * Load private config (Lambda name, etc.). Called after auth.
 */
export async function loadAppConfig(): Promise<AppConfig> {
  const runtime = await fetchJson("/app-config.json");
  if (runtime) {
    config = { ...config, ...runtime };
  }
  return config;
}


const TELEMETRY_HEADER = "---- Support Telemetry - Do Not Delete ----";

let _buildVersion: string | null = null;
async function getBuildVersion(): Promise<string> {
  if (_buildVersion) return _buildVersion;
  try {
    const resp = await fetch("/build-info.json");
    if (resp.ok) {
      const info = await resp.json();
      _buildVersion = info.build ?? info.version ?? "unknown";
      return _buildVersion;
    }
  } catch { /* ignore */ }
  return "unknown";
}

export function buildSupportUrl(opts: { error?: string; requestId?: string; sessionId?: string; version?: string; projectName?: string }): string {
  const officeVersion = typeof Office !== "undefined" ? Office?.context?.diagnostics?.version || "N/A" : "N/A";
  const telemetry = [
    `Source: Paebbl Data Browser Excel Addin`,
    `Project: ${opts.projectName || "N/A"}`,
    `Error: ${opts.error || "N/A"}`,
    `Request ID: ${opts.requestId || "N/A"}`,
    `Session ID: ${opts.sessionId || "N/A"}`,
    `Add-in Version: ${opts.version || _buildVersion || "N/A"}`,
    `Office Version: ${officeVersion}`,
    `Timestamp: ${new Date().toISOString()}`,
  ].join("\n");

  const description = `\n\n\n${TELEMETRY_HEADER}\n${telemetry}`;
  const summary = opts.error ? `XLAI Error: ${opts.error}` : "";

  const params = new URLSearchParams();
  if (summary) params.set("summary", summary);
  params.set("description", description);
  params.set(config.jsmSystemField, config.jsmPaebblSystemOptionId);

  return `${config.supportUrl}?${params.toString()}`;
}

export function buildSupportUrlSessionOnly(sessionId?: string | null, projectName?: string | null): string {
  const officeVersion = typeof Office !== "undefined" ? Office?.context?.diagnostics?.version || "N/A" : "N/A";
  const description = `\n\n\n${TELEMETRY_HEADER}\nSource: Paebbl Data Browser Excel Addin\nProject: ${projectName || "N/A"}\nSession ID: ${sessionId || "N/A"}\nAdd-in Version: ${_buildVersion || "N/A"}\nOffice Version: ${officeVersion}`;
  const params = new URLSearchParams();
  params.set("description", description);
  params.set(config.jsmSystemField, config.jsmPaebblSystemOptionId);
  return `${config.supportUrl}?${params.toString()}`;
}

export { getBuildVersion };